import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ShieldCheck } from "lucide-react";
import { useComplianceExpiring, COMPLIANCE_LABEL, type ExpiringItem } from "@/hooks/useCompliance.js";
import { ComplianceDrawer } from "@/components/ComplianceDrawer.js";

function getTitleIt(title: unknown): string {
  if (title && typeof title === "object" && "it" in title) return String((title as { it: unknown }).it ?? "");
  return "";
}
const fmtDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

export default function Compliance(): JSX.Element {
  const [days, setDays] = useState(60);
  const [open, setOpen] = useState<{ id: string; label: string } | null>(null);
  const { data, isLoading, error } = useComplianceExpiring(days);
  const items = data?.items ?? [];
  const today = data?.today ?? new Intl.DateTimeFormat("en-CA").format(new Date());
  const expired = items.filter((i) => i.expires_on !== null && i.expires_on < today).length;

  return (
    <div className="space-y-[22px]">
      <header>
        <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Adempimenti</h1>
        <p className="mt-[5px] text-[13.5px] text-ink-soft">
          {isLoading ? "Carico…" : `${items.length} in scadenza entro ${days} giorni${expired > 0 ? `, di cui ${expired} già ${expired === 1 ? "scaduto" : "scaduti"}` : ""}`}
        </p>
      </header>

      {error ? (
        <p className="rounded-[5px] border border-risk/30 bg-risk/10 p-3 text-[13px] text-risk">Errore: {(error as Error).message}</p>
      ) : null}

      <div className="ticks relative rounded-[5px] border border-rule bg-surface">
        <div className="flex items-center gap-[10px] border-b border-rule p-[13px_16px]">
          <CalendarClock className="size-4 text-accent" />
          <h3 className="text-[13px] font-semibold">In scadenza</h3>
          <label className="ml-auto flex items-center gap-[7px] text-[12px] text-ink-soft">
            Entro
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              aria-label="Finestra giorni"
              className="h-[28px] cursor-pointer rounded-[5px] border border-rule bg-surface-2 px-2 font-mono text-[12px] text-ink outline-none [&>option]:bg-surface [&>option]:text-ink"
            >
              {[30, 60, 90].map((d) => <option key={d} value={d}>{d} giorni</option>)}
            </select>
          </label>
        </div>

        {isLoading ? (
          <p className="p-[18px] text-[13px] text-ink-mute">Carico…</p>
        ) : items.length === 0 ? (
          <div className="flex items-center gap-3 p-[24px]">
            <ShieldCheck className="size-5 text-success" />
            <p className="text-[13px] text-ink-soft">Nessun adempimento in scadenza. Le scadenze si impostano dalla checklist di ogni immobile (lista Immobili, colonna Doc).</p>
          </div>
        ) : (
          <div>
            {items.map((row) => (
              <ExpiringRow key={`${row.listing_id}-${row.item}`} row={row} today={today} onOpen={setOpen} />
            ))}
          </div>
        )}
      </div>

      <ComplianceDrawer listingId={open?.id ?? null} listingLabel={open?.label} onClose={() => setOpen(null)} />
    </div>
  );
}

function ExpiringRow({ row, today, onOpen }: {
  row: ExpiringItem;
  today: string;
  onOpen: (v: { id: string; label: string }) => void;
}): JSX.Element {
  const isExpired = row.expires_on !== null && row.expires_on < today;
  const label = row.listing ? (getTitleIt(row.listing.title) || row.listing.slug) : "Immobile";
  return (
    <div className="flex flex-wrap items-center gap-[12px] border-b border-rule-soft p-[12px_16px] last:border-b-0 hover:bg-surface-2">
      <span className={`inline-flex h-[24px] items-center rounded-full border px-[10px] font-mono text-[11px] ${isExpired ? "border-risk/50 bg-risk/10 text-risk" : "border-warn/50 bg-warn/10 text-warn"}`}>
        {isExpired ? "Scaduto" : "Scade"} · {row.expires_on ? fmtDate(row.expires_on) : "—"}
      </span>
      <span className="text-[13px] font-semibold text-ink">{COMPLIANCE_LABEL[row.item]}</span>
      <span className="min-w-0 truncate text-[13px] text-ink-soft">
        {row.listing ? (
          <Link to={`/listings/${row.listing.id}`} className="hover:text-accent">{label}</Link>
        ) : label}
        {row.listing?.city ? <span className="text-ink-mute"> · {row.listing.city}</span> : null}
      </span>
      <button
        type="button"
        onClick={() => onOpen({ id: row.listing_id, label })}
        className="ml-auto h-[26px] rounded-[5px] border border-rule bg-surface-2 px-[10px] text-[12px] text-ink-soft transition-colors hover:border-ink-mute hover:text-ink"
      >
        Apri checklist
      </button>
    </div>
  );
}
